import * as z from 'zod';
import { http, unwrapData } from '@noor/api';
import { normalizeViewport } from '../model/viewport';
import {
  trackingSchema,
  trackingCourierSchema,
  trackingOrderSchema,
  searchOrderSchema,
  companyMarkSchema,
  equipmentSchema,
  type TrackingFilter,
} from '../model/schema';

function query(filter: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(filter)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)]),
  );
}
const rows = <T>(schema: z.ZodType<T>, raw: unknown): T[] => {
  const data = unwrapData(raw);
  return z
    .array(schema)
    .parse(data && typeof data === 'object' && 'data' in data ? data.data : data);
};
function trackingPayload(raw: unknown): unknown {
  // The legacy endpoint normally returns `{ couriers, stats }`. Some gateway
  // deployments wrap that in `data`, while others return the courier list as
  // `data` and keep `stats` alongside it. Accept those equivalent envelopes.
  if (Array.isArray(raw)) return { couriers: raw, stats: {} };
  if (!raw || typeof raw !== 'object') return raw;
  const envelope = raw as Record<string, unknown>;
  if (Array.isArray(envelope.couriers)) return envelope;
  if (Array.isArray(envelope.data)) {
    return { couriers: envelope.data, stats: envelope.stats ?? {} };
  }
  const data = unwrapData(raw);
  if (Array.isArray(data)) return { couriers: data, stats: envelope.stats ?? {} };
  return data;
}
function parseJsonOrText(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}
export const trackingApi = {
  async couriers(filter: TrackingFilter) {
    const raw = await http.get<unknown>('couriers/in-radius', {
      // The legacy Axios client parsed JSON even when this endpoint returned
      // a missing or incorrect Content-Type. Force JSON parsing here too.
      parseResponse: parseJsonOrText,
      query: query(normalizeViewport(filter)),
    });
    const parsed = trackingSchema.safeParse(trackingPayload(raw));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const field = issue?.path.length ? issue.path.join('.') : 'response';
      throw new Error(
        `Courier tracking response is not valid JSON data (${field}: ${issue?.message ?? 'invalid response'})`,
        { cause: parsed.error },
      );
    }
    return parsed.data;
  },
  async courier(id: string | number) {
    return trackingCourierSchema.parse(
      unwrapData(await http.get<unknown>(`admin/couriers/${id}`)),
    );
  },
  async location(id: string | number) {
    return z
      .object({
        location: z.object({ lat: z.number(), long: z.number() }).nullish(),
        last_reported_at: z.string().nullish(),
      })
      .parse(unwrapData(await http.get<unknown>(`admin/couriers/${id}/cache`)));
  },
  async stations() {
    return rows(
      companyMarkSchema,
      await http.get<unknown>('admin/company/locations', {
        query: { limit: '100', type: 'CHARGING' },
      }),
    );
  },
  async searchOrders() {
    return rows(searchOrderSchema, await http.get<unknown>('admin/orders/in-search'));
  },
  async activeOrders() {
    return rows(
      trackingOrderSchema,
      await http.get<unknown>('admin/orders', {
        query: { is_active: 'true', limit: '1000' },
      }),
    );
  },
  async resolveOrder(displayId: string | number) {
    const result = rows(
      trackingOrderSchema,
      await http.get<unknown>('admin/orders', {
        query: { display_id: String(displayId), limit: '1', skip: '0' },
      }),
    );
    return result[0]?.id ?? null;
  },
  async todayOrders(
    courierId: string | number,
    skip: number,
    limit: number,
    from: string,
    till: string,
  ) {
    const raw = await http.get<unknown>('admin/orders', {
      query: query({
        courier_id: courierId,
        skip,
        limit,
        from_date: from,
        till_date: till,
      }),
    });
    return z
      .object({ data: z.array(trackingOrderSchema), count: z.coerce.number() })
      .parse(raw && typeof raw === 'object' && 'count' in raw ? raw : unwrapData(raw));
  },
  async equipment(search: string, skip: number) {
    const raw = await http.get<unknown>('admin/equipments', {
      query: query({ search, skip, limit: 15 }),
    });
    return z
      .object({ data: z.array(equipmentSchema), count: z.coerce.number() })
      .parse(raw && typeof raw === 'object' && 'count' in raw ? raw : unwrapData(raw));
  },
  async courierOptions(search: string, skip: number) {
    const raw = await http.get<unknown>('admin/couriers', {
      query: query({ search, skip, limit: 15, is_active: true }),
    });
    return z
      .object({ data: z.array(trackingCourierSchema), count: z.coerce.number() })
      .parse(raw && typeof raw === 'object' && 'count' in raw ? raw : unwrapData(raw));
  },
};
